// components/mapa/MapaViewer.tsx
'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import { getMapaById } from '@/lib/services/mapas';
import { getTiposEntrada } from '@/lib/services/tipoEntrada';

/** Tipos de entrada disponibles para el mapa */
type TipoEntrada = { id: string; nombre: string; color?: string };

/** Payload que enviamos al padre cuando el usuario toca/selecciona una silla */
export type SeatTogglePayload = {
  seatId: string;        // id del asiento (elemento del SVG)
  mesaId: string;        // id de la mesa/grupo al que pertenece
  tipoEntradaId: string; // tipo de entrada de esa mesa
  selected: boolean;     // true si quedó seleccionada tras el click
};

type MapaViewerProps = {
  /** Si querés que el componente se cargue solo por id */
  mapaId?: string;
  /** O podés pasar el objeto de mapa ya cargado */
  initialMapa?: any | null;
  /** Tipos de entrada (si no los pasás, se piden) */
  initialTipos?: TipoEntrada[] | null;

  occupiedSeats?: string[];
  conflictSeats?: string[];
  selectedSeats?: string[];

  /** Clases extra para el contenedor externo */
  className?: string;
  /** Te avisamos cada vez que el usuario togglea una silla */
  onSeatToggle?: (p: SeatTogglePayload) => void;

  disabled?: boolean;
};

/** Selectores "candidatos" a asiento dentro del SVG */
const SEAT_SELECTOR =
  '[data-seat-id], [data-asiento-id], .seat, .silla, [id^="seat-"], [id^="asiento-"]';

/** Color para asientos ocupados */
const OCCUPIED_FILL = '#374151'; // gray-700

/** Color para asientos en conflicto */
const CONFLICT_FILL = '#ef4444'; // red-500 (más intenso)

export default function MapaViewer({
  mapaId,
  initialMapa = null,
  initialTipos = null,
  occupiedSeats = [],
  conflictSeats = [],
  selectedSeats = [],
  className,
  onSeatToggle,
  disabled = false,
}: MapaViewerProps) {
  const [mapa, setMapa] = useState<any | null>(initialMapa);
  const [tipos, setTipos] = useState<TipoEntrada[]>(initialTipos ?? []);
  const [svgSanitized, setSvgSanitized] = useState<string>('');

  const ocupados = useMemo(() => new Set(occupiedSeats), [occupiedSeats]);
  const conflictos = useMemo(() => new Set(conflictSeats), [conflictSeats]);
  const seleccionados = useMemo(() => new Set(selectedSeats), [selectedSeats]);

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const svgContainerRef = useRef<HTMLDivElement>(null);

  /* ──────────────────────────────────────────────
   *  Carga (si no vino por props)
   * ────────────────────────────────────────────── */
  useEffect(() => {
    const needMapa = !initialMapa && mapaId;
    const needTipos = !initialTipos;
    if (!needMapa && !needTipos) return;

    (async () => {
      try {

        setLoading(true);
        setError(null);
        const [m, t] = await Promise.all([
          needMapa ? getMapaById(String(mapaId)) : Promise.resolve(initialMapa),
          needTipos ? getTiposEntrada() : Promise.resolve(initialTipos),
        ]);
        setMapa(m ?? null);
        setTipos((t ?? []) as TipoEntrada[]);
      } catch {
        setError('No pude cargar datos del mapa o los tipos de entrada.');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapaId, initialMapa, setSvgSanitized]);

  /* ──────────────────────────────────────────────
   *  Índice de color por tipo
   * ────────────────────────────────────────────── */
  const colorByTipoId = useMemo(() => {
    const m = new Map<string, string>();
    tipos.forEach((t) => m.set(t.id, t.color || '#7c3aed')); // violet-700 por defecto
    return m;
  }, [tipos]);

  /* ──────────────────────────────────────────────
   *  Mesa -> tipoEntradaId (de mapa.mesas o mapping_json.tables)
   * ────────────────────────────────────────────── */
  const mesaTipoMap: Record<string, string | null> = useMemo(() => {
    if (!mapa) return {};
    const out: Record<string, string | null> = {};

    // formato legado
    const mesas = Array.isArray(mapa?.mesas) ? mapa.mesas : [];
    mesas.forEach((mm: any) => {
      out[String(mm.mesaId)] = mm?.tipoEntradaId ?? null;
    });

    // mapping_json actual
    const tables = Array.isArray(mapa?.mapping_json?.tables) ? mapa.mapping_json.tables : [];
    tables.forEach((tb: any) => {
      const id = String(tb?.id ?? '');
      if (!id) return;
      if (out[id] === undefined) {
        out[id] = tb?.tipo_entrada_id != null ? String(tb.tipo_entrada_id) : null;
      }
    });

    return out;
  }, [mapa]);

  /* ──────────────────────────────────────────────
   *  seatId -> mesaId (de mapping_json.tables[].seats)
   * ────────────────────────────────────────────── */
  const seatMesaMap = useMemo(() => {
    const map = new Map<string, string>();
    const tables = Array.isArray(mapa?.mapping_json?.tables) ? mapa.mapping_json.tables : [];
    for (const tb of tables) {
      const mesaId = String(tb?.id ?? '');
      const seats = Array.isArray(tb?.seats) ? tb.seats : [];
      for (const s of seats) {
        const seatId = String(s?.id ?? s);
        if (seatId) map.set(seatId, mesaId);
      }
    }
    return map;
  }, [mapa]);

  /* ──────────────────────────────────────────────
   *  Sanitizar el SVG (permitiendo data-seat-id / data-asiento-id)
   * ────────────────────────────────────────────── */
  useEffect(() => {
    const container = svgContainerRef.current;
    if (!container || !mapa?.svg) return;

    // Sanitiza el SVG (ya tenías DOMPurify)
    const clean = DOMPurify.sanitize(mapa.svg, {
      ADD_TAGS: ['svg'],
      ADD_ATTR: [
        'xmlns',
        'viewBox',
        'x',
        'y',
        'width',
        'height',
        'fill',
        'stroke',
        'stroke-width',
        'id',
        'class',
        'transform',
        'd',
        'cx',
        'cy',
        'r',
        'points',
        'data-seat-id',
        'data-asiento-id',
        'style',
      ],
    });

    // Limpio lo anterior
    container.innerHTML = '';

    // 👇 Parseamos el string como SVG real (con namespace)
    const parser = new DOMParser();
    const doc = parser.parseFromString(clean, 'image/svg+xml');
    const svgNode = doc.querySelector('svg');

    if (svgNode) {
      // Aseguramos namespace
      if (!svgNode.getAttribute('xmlns')) {
        svgNode.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      }
      container.appendChild(svgNode);
    }

    // Ahora el SVG es parte real del DOM → reacciona a cambios de style/attr
  }, [mapa]);

  /* ──────────────────────────────────────────────
   *  Inyectar labels de mesa y asiento en el SVG
   * ────────────────────────────────────────────── */
  useEffect(() => {
    const container = svgContainerRef.current;
    if (!container || !mapa?.mapping_json) return;
    const svgEl = container.querySelector('svg');
    if (!svgEl) return;

    const SVG_NS = 'http://www.w3.org/2000/svg';

    // Limpiar labels previos
    svgEl.querySelectorAll('.tk-label').forEach((el) => el.remove());

    const tables = Array.isArray(mapa.mapping_json.tables) ? mapa.mapping_json.tables : [];

    for (const table of tables) {
      const tableId = String(table?.id ?? '');
      if (!tableId) continue;

      // ── Label de mesa ──
      const tableEl = svgEl.querySelector<SVGElement>(`[id="${tableId}"]`);
      if (tableEl) {
        let cx: number | null = null;
        let cy: number | null = null;

        // Calcular centro en coordenadas del SVG root (no locales)
        if (typeof (tableEl as SVGGraphicsElement).getBBox === 'function') {
          try {
            const bbox = (tableEl as SVGGraphicsElement).getBBox();
            const localCx = bbox.x + bbox.width / 2;
            const localCy = bbox.y + bbox.height / 2;

            // Transformar de espacio local del elemento a espacio del SVG root
            const elCtm = (tableEl as SVGGraphicsElement).getCTM();
            const svgCtm = (svgEl as SVGSVGElement).getCTM();

            if (elCtm && svgCtm) {
              const point = (svgEl as SVGSVGElement).createSVGPoint();
              point.x = localCx;
              point.y = localCy;
              const global = point.matrixTransform(svgCtm.inverse().multiply(elCtm));
              cx = global.x;
              cy = global.y;
            } else {
              cx = localCx;
              cy = localCy;
            }
          } catch { /* getBBox puede fallar si el elemento no tiene tamaño */ }
        }

        if (cx != null && cy != null) {
          const labelText = (table.label || tableId).replace(/^table-/i, '');
          const text = document.createElementNS(SVG_NS, 'text');
          text.setAttribute('x', String(cx));
          text.setAttribute('y', String(cy));
          text.setAttribute('text-anchor', 'middle');
          text.setAttribute('dominant-baseline', 'central');
          text.setAttribute('font-size', '10');
          text.setAttribute('font-family', 'sans-serif');
          text.setAttribute('font-weight', 'bold');
          text.setAttribute('fill', 'white');
          text.setAttribute('pointer-events', 'none');
          text.setAttribute('paint-order', 'stroke');
          text.setAttribute('stroke', 'rgba(0,0,0,0.5)');
          text.setAttribute('stroke-width', '2');
          text.classList.add('tk-label', 'tk-mesa-label');
          text.textContent = labelText;
          svgEl.appendChild(text);
        }
      }

    }

    return () => {
      svgEl.querySelectorAll('.tk-label').forEach((el) => el.remove());
    };
  }, [mapa]);

  /* ──────────────────────────────────────────────
   *  Pintar las MESAS según su tipo (color)
   * ────────────────────────────────────────────── */
  useEffect(() => {
    const container = svgContainerRef.current;
    if (!container) return;
    const svgEl = container.querySelector('svg');
    if (!svgEl) return;

    Object.entries(mesaTipoMap).forEach(([mesaId, tipoId]) => {
      if (!mesaId || !tipoId) return;
      const fill = colorByTipoId.get(tipoId) || '#7c3aed';
      const el = container.querySelector<SVGElement>(`[id="${mesaId}"]`);
      if (el) el.setAttribute('fill', fill);
    });
  },);

  /* ──────────────────────────────────────────────
   *  Marcar asientos ocupados (gris + no clickeable)
   * ────────────────────────────────────────────── */
  useEffect(() => {
    const container = svgContainerRef.current;
    if (!container) return;

    // Primero: limpiar todos los asientos marcados como ocupados
    const previouslyOccupied = container.querySelectorAll<SVGElement>('.tk-seat-occupied');
    previouslyOccupied.forEach((el) => {
      el.classList.remove('tk-seat-occupied');
      el.style.pointerEvents = '';
      el.removeAttribute('stroke');

      // Restaurar fill original de los hijos
      const children = el.querySelectorAll<SVGElement>('path, rect, circle');
      children.forEach((child) => {
        const originalFill = child.dataset.originalOccupiedFill;
        if (originalFill) {
          child.setAttribute('fill', originalFill);
          delete child.dataset.originalOccupiedFill;
        }
      });
    });

    // Si no hay ocupados, no hacer nada más
    if (ocupados.size === 0) return;

    // Buscar todos los seats y marcar los ocupados
    seatMesaMap.forEach((_, seatId) => {
      if (!ocupados.has(seatId)) return;

      // No marcar como ocupado si está en conflicto (el efecto de conflictos lo maneja)
      if (conflictos.has(seatId)) return;

      const el = container.querySelector<SVGElement>(`[id="${seatId}"]`);
      if (!el) return;

      // Marcar como ocupado
      el.classList.add('tk-seat-occupied');
      el.style.pointerEvents = 'none';
      el.setAttribute('stroke', 'black');

      // Pintar hijos en gris (guardando original)
      const children = el.querySelectorAll<SVGElement>('path, rect, circle');
      children.forEach((child) => {
        if (!child.dataset.originalOccupiedFill) {
          child.dataset.originalOccupiedFill = child.getAttribute('fill') || '';
        }
        child.setAttribute('fill', OCCUPIED_FILL);
      });
    });
  }, [ocupados, conflictos, seatMesaMap]);

  /* ──────────────────────────────────────────────
   *  Marcar asientos en conflicto (rojo + clickeable)
   * ────────────────────────────────────────────── */
  useEffect(() => {
    const container = svgContainerRef.current;
    if (!container) return;

    // Primero: limpiar todos los asientos marcados como conflicto
    const previouslyConflict = container.querySelectorAll<SVGElement>('.tk-seat-conflict');
    previouslyConflict.forEach((el) => {
      el.classList.remove('tk-seat-conflict');
      el.style.pointerEvents = '';
      el.removeAttribute('stroke');

      // Restaurar fill original de los hijos
      const children = el.querySelectorAll<SVGElement>('path, rect, circle');
      children.forEach((child) => {
        const originalFill = child.dataset.originalConflictFill;
        if (originalFill) {
          child.setAttribute('fill', originalFill);
          delete child.dataset.originalConflictFill;
        }
      });
    });

    // Si no hay conflictos, no hacer nada más
    if (conflictos.size === 0) return;

    // Buscar todos los seats y marcar los que están en conflicto
    seatMesaMap.forEach((_, seatId) => {
      if (!conflictos.has(seatId)) return;

      const el = container.querySelector<SVGElement>(`[id="${seatId}"]`);
      if (!el) return;

      // Marcar como conflicto (clickeable para que el usuario pueda deseleccionar)
      el.classList.add('tk-seat-conflict');
      el.setAttribute('stroke', '#b91c1c'); // red-700
      el.setAttribute('stroke-width', '2');

      // Pintar hijos en rojo (guardando original)
      const children = el.querySelectorAll<SVGElement>('path, rect, circle');
      children.forEach((child) => {
        if (!child.dataset.originalConflictFill) {
          child.dataset.originalConflictFill = child.getAttribute('fill') || '';
        }
        child.setAttribute('fill', CONFLICT_FILL);
      });
    });
  }, [conflictos, seatMesaMap]);

  /* ──────────────────────────────────────────────
   *  Marcar asientos seleccionados (restaurar estado visual)
   * ────────────────────────────────────────────── */
  useEffect(() => {
    const container = svgContainerRef.current;
    if (!container) return;

    // Primero: limpiar todos los asientos que estaban seleccionados pero ya no lo están
    const previouslySelected = container.querySelectorAll<SVGElement>('.tk-seat-selected');
    previouslySelected.forEach((el) => {
      const seatId = el.id;
      // Si ya no está en la lista de seleccionados, limpiar
      if (!seleccionados.has(seatId)) {
        el.classList.remove('tk-seat-selected');
        el.removeAttribute('stroke');
        el.removeAttribute('stroke-width');

        // Restaurar fill original de los hijos
        const children = el.querySelectorAll<SVGElement>('path, rect, circle');
        children.forEach((child) => {
          const originalFill = child.dataset.originalSelectedFill;
          if (originalFill !== undefined) {
            child.setAttribute('fill', originalFill);
            delete child.dataset.originalSelectedFill;
          }
        });
      }
    });

    // Si no hay seleccionados, terminar aquí
    if (seleccionados.size === 0) return;

    // Marcar los asientos que vienen como seleccionados (excluir ocupados y conflictos)
    seatMesaMap.forEach((mesaId, seatId) => {
      if (!seleccionados.has(seatId)) return;
      if (ocupados.has(seatId)) return; // no marcar si está ocupado
      if (conflictos.has(seatId)) return; // no marcar si está en conflicto (ya tiene su estilo)

      const el = container.querySelector<SVGElement>(`[id="${seatId}"]`);
      if (!el) return;

      // Marcar como seleccionado
      el.classList.add('tk-seat-selected');

      // Obtener color del tipo de entrada
      const tipoEntradaId = mesaTipoMap[mesaId] ?? null;
      const fillColor = tipoEntradaId ? colorByTipoId.get(tipoEntradaId) ?? 'white' : 'white';

      el.setAttribute('stroke', fillColor);
      el.setAttribute('stroke-width', '1');

      // Pintar hijos con el color (guardando original)
      const children = el.querySelectorAll<SVGElement>('path, rect, circle');
      children.forEach((child) => {
        if (!child.dataset.originalSelectedFill) {
          child.dataset.originalSelectedFill = child.getAttribute('fill') || '';
        }
        child.setAttribute('fill', fillColor);
      });
    });
  }, [seleccionados, ocupados, conflictos, seatMesaMap, mesaTipoMap, colorByTipoId]);

  /* ──────────────────────────────────────────────
   *  Diagnóstico: cuántos seats del mapping existen en el SVG
   * ────────────────────────────────────────────── */
  useEffect(() => {
    const container = svgContainerRef.current;
    if (!container) return;
    let found = 0,
      missing = 0;
    seatMesaMap.forEach((_mesaId, seatId) => {
      const el = container.querySelector<SVGElement>(`[id="${seatId}"]`);
      el ? found++ : missing++;
    });

  }, [seatMesaMap, svgSanitized]);

  /* ──────────────────────────────────────────────
   *  Click handler para togglear selección de sillas
   * ────────────────────────────────────────────── */
  useEffect(() => {
    const container = svgContainerRef.current;
    if (!container) return;

    const handler = (ev: MouseEvent) => {
      const raw = ev.target as Element | null;
      if (!raw) return;

      // Busco el elemento con id más cercano (a veces clickeás un path hijo)
      // Primero buscar por clase de conflicto/seleccionado, luego por id de seat
      let withId = raw.closest('.tk-seat-conflict, .tk-seat-selected') as SVGElement | null;

      // Si no encontró por clase, buscar por id de seat
      if (!withId) {
        withId = raw.closest('g[id^="seat"], [id^="seat"]') as SVGElement | null;
      }

      // Último recurso: buscar cualquier elemento con id que esté en el seatMesaMap
      if (!withId) {
        let current = raw as Element | null;
        while (current && current !== svgContainerRef.current) {
          const id = current.id;
          if (id && seatMesaMap.has(id)) {
            withId = current as SVGElement;
            break;
          }
          current = current.parentElement;
        }
      }

      if (!withId || !withId.id) return;

      const isCurrentlySelected = withId.classList.contains('tk-seat-selected') || withId.classList.contains('tk-seat-conflict');
      if (disabled && !isCurrentlySelected) return;

      let seatId = withId.id || '';
      let mesaId: string | undefined;

      // 1) seat mapeado explícito
      if (seatMesaMap.has(seatId)) {
        mesaId = seatMesaMap.get(seatId)!;
      } else {
        // 2) si parece silla (class "seat" o id que contiene seat/silla)
        const looksLikeSeat = withId.classList.contains('seat') || /seat|silla/i.test(seatId);
        if (looksLikeSeat) {
          // 3) subo por ancestros hasta encontrar una mesa conocida
          let cur: Element | null = withId;
          while (cur && !mesaId) {
            const cid = (cur as any).id as string | undefined;
            if (cid && mesaTipoMap[cid] != null) mesaId = cid;
            cur = cur.parentElement;
          }
        }
      }

      if (!mesaId) return; // no es silla
      const tipoEntradaId = mesaTipoMap[mesaId] ?? null;
      if (!tipoEntradaId) return; // mesa sin tipo → ignorar

      // Verificar si es un conflicto ANTES de checkear ocupados
      const isConflict = conflictos.has(seatId) || withId.classList.contains('tk-seat-conflict');

      // Ignorar asientos ocupados (pero permitir conflictos para que se puedan deseleccionar)
      if (ocupados.has(seatId) && !isConflict) return;

      if (isConflict) {
        // Quitar clase de conflicto y seleccionado
        withId.classList.remove('tk-seat-conflict', 'tk-seat-selected');
        // Marcar como ocupado visualmente
        withId.classList.add('tk-seat-occupied');
        withId.style.pointerEvents = 'none';
        withId.setAttribute('stroke', 'black');
        withId.setAttribute('stroke-width', '1');

        // Pintar hijos en gris
        const children = withId.querySelectorAll<SVGElement>('path, rect, circle');
        children.forEach((child) => {
          child.setAttribute('fill', OCCUPIED_FILL);
        });

        // Notificar deselección
        onSeatToggle?.({
          seatId: seatId || withId.id,
          mesaId,
          tipoEntradaId,
          selected: false,
        });
        return;
      }

      const fillColor = colorByTipoId.get(tipoEntradaId) ?? 'white'

      // Toggle visual
      const selected = !withId.classList.contains('tk-seat-selected');
      withId.classList.toggle('tk-seat-selected', selected);

      const children = withId.querySelectorAll<SVGElement>('path, rect, circle');

      if (selected) {
        withId.setAttribute('stroke', fillColor);
        withId.setAttribute('stroke-width', '1');

        // Pintar hijos con el color (guardando original)
        children.forEach((child) => {
          if (!child.dataset.originalSelectedFill) {
            child.dataset.originalSelectedFill = child.getAttribute('fill') || '';
          }
          child.setAttribute('fill', fillColor);
        });
      } else {
        withId.removeAttribute('stroke');
        withId.removeAttribute('stroke-width');

        // Restaurar fill original de los hijos
        children.forEach((child) => {
          const originalFill = child.dataset.originalSelectedFill;
          if (originalFill !== undefined) {
            child.setAttribute('fill', originalFill);
            delete child.dataset.originalSelectedFill;
          }
        });
      }

      onSeatToggle?.({
        seatId: seatId || withId.id,
        mesaId,
        tipoEntradaId,
        selected,
      });

      // Log útil
      //console.table({ seatId, mesaId, tipoEntradaId, selected });
    };

    container.addEventListener('click', handler);
    return () => container.removeEventListener('click', handler);
  }, [seatMesaMap, mesaTipoMap, ocupados, conflictos, colorByTipoId, onSeatToggle, disabled]);

  /* ──────────────────────────────────────────────
   *  Render
   * ────────────────────────────────────────────── */
  return (
    <div className={className}>
      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 p-3 rounded-lg mb-3">
          {error}
        </div>
      )}

      <div className="rounded-xl border border-violet-300 bg-white overflow-hidden">
        {/* Header con leyenda */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-violet-200">
          <div className="flex items-center gap-4 text-xs">
            <span className="inline-flex items-center gap-1.5 text-gray-700">
              <span className="inline-block h-3 w-3 rounded-full border-2 border-gray-400 bg-white" /> Disponible
            </span>
            <span className="inline-flex items-center gap-1.5 text-gray-700">
              <span className="inline-block h-3 w-3 rounded-full" style={{ backgroundColor: OCCUPIED_FILL }} /> Reservado
            </span>
            {conflictSeats && conflictSeats.length > 0 && (
              <span className="inline-flex items-center gap-1.5 text-red-600 font-medium">
                <span className="inline-block h-3 w-3 rounded-full" style={{ backgroundColor: CONFLICT_FILL }} /> No disponible
              </span>
            )}
          </div>
        </div>

        {/* Contenedor scroll del SVG */}
        <div className="p-3">
          <div
            ref={svgContainerRef}
            className="relative max-w-full overflow-auto bg-gray-100 rounded-md"
            style={{ minHeight: 260 }}

          />
        </div>
      </div>

      {/* Estilo del glow de selección (opcional) */}
      <style jsx>{`
        :global(.tk-seat-selected) {
          fill: "blue"
        }
        :global(.tk-seat-occupied) {
          cursor: not-allowed !important;
          opacity: 0.6;
        }
        :global(svg ${SEAT_SELECTOR}) {
          cursor: pointer;
        }
      `}</style>
    </div>
  );
}
